import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { makeStyles, CircularProgress, useTheme } from '~/lib/bn-material-ui';
import InventoryMapContainer from './inventory-map-container';
import InventoryMapBinList from './inventory-map-bin-list';
import InventoryMapBinDetailSidebar from './inventory-map-bin-detail-sidebar';
import InventoryMapHighlightControls, { HighlightModes } from './inventory-map-highlight-controls';
import InventoryMapFilters from './inventory-map-filters';
import { exampleBackgroundShapes } from './demo-data';
import { useMillBinsGroup } from '../use-mill-bins-group';
import { useQuery } from '../use-query';
import { useSirenEntity, sirenActions, entityStore } from '~/utility/BinSentry-ui-utility';
import { createMaterialColorMap, DEFAULT_MATERIAL_COLOR } from './material-colors';
import { getLatestBinLevelEntityFromPageNow } from '../bin-props';

const useStyles = makeStyles(() => ({
  container: {
    height: 'calc(100dvh - 153px)',
    '@media only screen and (max-width: 1280px)': {
      height: 'calc(100dvh - 132px)',
    },
  },
  mainContent: {
    display: 'flex',
    height: '100%',
  },
  mapContainer: {
    flex: 1,
    height: '100%',
    position: 'relative',
    display: 'flex',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
  },
}));

const binShapeToMapShape = {
  rectangular: 'rectangle',
  circular: 'circle',
};

function useBinsForMap(bins) {
  const [binsForMap, setBinsForMap] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!bins) {
      setBinsForMap([]);
      return;
    }

    const loadBinData = async () => {
      setLoading(true);
      const newBinsForMap = await Promise.all(bins.map(async binEntity => {
        const properties = binEntity.properties;
        const dimensions = properties.dimensions;

        if (!properties.location?.latitude || !properties.location?.longitude) {
          // Filter out bins with no location
          return null;
        }

        const [latestLevelEntity, { entity: tagsEntity }] = await Promise.all([
          getLatestBinLevelEntityFromPageNow({ binEntity }),
          // entityStore.fetchWait(binEntity.getSubEntityByRel('https://api.binsentry.com/rel/bin-level-latest')?.href, false),
          entityStore.fetchWait(binEntity.getSubEntityByRel('https://api.binsentry.com/rel/tags')?.href, false),
        ]);

        const tags = tagsEntity?.entities.filter(tag => tag.class.includes('tag')) || [];
        const material = tags.find(tag => tag.properties.key === '_material')?.properties.value;
        const binType = tags.find(tag => tag.properties.key === '_binType')?.properties.value;

        // Determine shape and dimensions for map display
        let mapDimensions = {};
        let shape = binShapeToMapShape[dimensions?.shape];
        if (dimensions?.shape === 'circular') {
          mapDimensions = { radius: dimensions.body.radius };
        } else {
          mapDimensions = {
            width: dimensions.body.width,
            height: dimensions.body.depth,
          };
        }

        return {
          id: binEntity.getLink('self').href,
          name: properties.name,
          latitude: properties.location?.latitude,
          longitude: properties.location?.longitude,
          shape,
          latestVolume: latestLevelEntity?.properties?.volume,
          fullTotal: binEntity.properties?.volumes?.fullTotal,
          material,
          binType,
          dimensions: mapDimensions,
          // Keep reference to original entities
          entity: binEntity,
          latestLevelEntity,
        };
      }).filter(Boolean));
      setLoading(false);
      setBinsForMap(newBinsForMap);
    };

    loadBinData();
  }, [bins]);

  return { binsForMap, loading };
}

export default function InventoryMapPage() {
  const styles = useStyles();
  const mapContainerRef = useRef(null);
  const searchTimeout = useRef(null);
  const [mapDimensions, setMapDimensions] = useState({ width: 800, height: 600 });
  const [selectedBinId, setSelectedBinId] = useState(null);
  const [highlightMode, setHighlightMode] = useState(HighlightModes.FILL_LEVEL);
  const [selectedBinType, setSelectedBinType] = useState('');
  const [selectedIngredient, setSelectedIngredient] = useState('');
  const [selectedLevelRange, setSelectedLevelRange] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const { millBinsGroup, loading: loadingOrg } = useMillBinsGroup();
  const { entity: millBinsGroupEntity } = useSirenEntity({ href: millBinsGroup?.getLinkByRel('self').href });
  const [{ sort = 'name', order = 'asc' }] = useQuery();
  const theme = useTheme();

  // Load all bins at once (not using search API for local filtering)
  const { entity: searchEntity } = useSirenEntity({ href: millBinsGroupEntity?.getLink('https://api.binsentry.com/rel/search-bins')?.href });
  const searchAction = searchEntity?.getAction('search-bins');
  const url = searchAction && sirenActions.parseAction(searchAction, {
    query: '', // No query - load all bins
    sort: 'name', // Default sort
    order: 'asc', // Default order
    limit: 1000, // Load all bins at once for local filtering
  }).url;

  const { entity: binsEntity, loading: loadingBins } = useSirenEntity({ href: url?.href });
  const allBins = useMemo(() => binsEntity?.getSubEntitiesByClass('physical-bin'), [binsEntity]);

  // Convert bin entities to map format
  const { binsForMap: binsForMapRaw, loading: loadingBinsForMap } = useBinsForMap(allBins);

  // Extract available bin types and materials for filters
  const availableBinTypes = useMemo(() => {
    if (!binsForMapRaw) {
      return [];
    }
    const types = [...new Set(binsForMapRaw.map(bin => bin.binType).filter(Boolean))];
    return types.sort();
  }, [binsForMapRaw]);

  const availableMaterials = useMemo(() => {
    if (!binsForMapRaw) {
      return [];
    }
    const materials = [...new Set(binsForMapRaw.map(bin => bin.material).filter(Boolean))];
    return materials.sort();
  }, [binsForMapRaw]);

  // Apply all filters including search query and add filtered boolean to each item
  const binsForMap = useMemo(() => {
    if (!binsForMapRaw) {
      return [];
    }

    return binsForMapRaw.map(item => {
      let isFiltered = false;

      // Check search query filter
      if (searchQuery && !item.name.toLowerCase().includes(searchQuery.toLowerCase())) {
        isFiltered = true;
      }

      // Check bin type filter
      if (selectedBinType && item.binType !== selectedBinType) {
        isFiltered = true;
      }

      // Check ingredient filter
      if (selectedIngredient && item.material !== selectedIngredient) {
        isFiltered = true;
      }

      // Check level range filter
      if (selectedLevelRange && item.latestVolume !== undefined && item.fullTotal && item.fullTotal > 0) {
        const fillPercentage = (item.latestVolume / item.fullTotal) * 100;
        let matchesLevelFilter = false;

        switch (selectedLevelRange) {
          case '0-25':
            matchesLevelFilter = fillPercentage >= 0 && fillPercentage < 25;
            break;
          case '25-50':
            matchesLevelFilter = fillPercentage >= 25 && fillPercentage < 50;
            break;
          case '50-75':
            matchesLevelFilter = fillPercentage >= 50 && fillPercentage < 75;
            break;
          case '75-100':
            matchesLevelFilter = fillPercentage >= 75 && fillPercentage <= 100;
            break;
          default:
            matchesLevelFilter = true;
        }

        if (!matchesLevelFilter) {
          isFiltered = true;
        }
      }

      return {
        ...item,
        isFiltered,
      };
    });
  }, [binsForMapRaw, searchQuery, selectedBinType, selectedIngredient, selectedLevelRange]);

  // Handle bin selection from list or map
  const handleBinSelect = useCallback((bin) => {
    const binId = bin.getLink ? bin.getLink('self').href : bin.id;
    setSelectedBinId(binId);
  }, []);

  const handleBinClick = useCallback((bin) => setSelectedBinId(bin.id), []);
  const handleCloseSidebar = useCallback(() => setSelectedBinId(null), []);

  // Handle search query changes with debouncing
  const onSearchQueryChange = useCallback((value) => {
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => setSearchQuery(value), 500);
  }, []);

  // Get selected bin entity
  const selectedBin = useMemo(() => {
    if (!selectedBinId || !allBins) {
      return null;
    }
    return allBins.find(bin => bin.getLink('self').href === selectedBinId);
  }, [selectedBinId, allBins]);

  // Create a consistent color mapping for materials
  const materialColorMap = useMemo(() => {
    if (!binsForMap) {
      return;
    }

    const materials = [...new Set(binsForMap.map(bin => bin.material).filter(Boolean))];
    return createMaterialColorMap(materials);
  }, [binsForMap]);

  const binStyles = useMemo(() => {
    const styles = {};
    if (!binsForMap) {
      return styles;
    }

    // Add selected bin highlighting
    if (selectedBinId) {
      binsForMap.forEach(item => {
        if (item.id === selectedBinId) {
          styles[item.id] = {
            filter: 'brightness(1.0125)',
            strokeWidth: 4,
            stroke: 'none',
            doubleStrokeEnabled: true,
            doubleStrokeColour: theme.palette.blue[40],
          };
          return;
        }
      });
    }

    // Apply filter highlighting using pre-computed isFiltered property
    binsForMap.forEach(item => {
      if (item.isFiltered) {
        styles[item.id] = {
          ...styles[item.id],
          opacity: 0.3,
          filter: 'grayscale(0.5)',
          textColor: theme.palette.gray[50],
        };
      }
    });

    // Apply highlight mode styling
    if (highlightMode === HighlightModes.FILL_LEVEL) {
      binsForMap.forEach(item => {
        const currentVolume = item.latestVolume;
        const fullTotal = item.fullTotal;

        // Calculate fill percentage if we have both values
        if (currentVolume !== undefined && fullTotal && fullTotal > 0) {
          const fillPercentage = (currentVolume / fullTotal) * 100;

          let fillColor;
          let fillLevelColor;
          if (fillPercentage >= 70) {
            fillColor = '#cdcdcdff';
            fillLevelColor = '#4caf50';
          } else if (fillPercentage >= 25) {
            fillColor = '#cdcdcdff';
            fillLevelColor = '#ff9800';
          } else {
            fillColor = '#cdcdcdff';
            fillLevelColor = '#f44336';
          }

          styles[item.id] = {
            fill: fillColor,
            fillLevelColor,
            ...styles[item.id] || {},
          };
        }
      });
    } else if (highlightMode === HighlightModes.MATERIAL) {
      binsForMap.forEach(item => {
        // Use consistent color based on material name
        const materialColor = item.material ? materialColorMap[item.material] : DEFAULT_MATERIAL_COLOR;

        styles[item.id] = {
          fill: materialColor,
          ...styles[item.id] || {},
        };
      });
    }

    return styles;
  }, [selectedBinId, highlightMode, binsForMap, materialColorMap, theme.palette.blue, theme.palette.gray]);

  // Update map dimensions when container resizes
  useEffect(() => {
    const updateMapDimensions = () => {
      if (mapContainerRef.current) {
        const rect = mapContainerRef.current.getBoundingClientRect();
        setMapDimensions({
          width: rect.width,
          height: rect.height,
        });
      }
    };

    // Initial measurement
    updateMapDimensions();

    // Set up resize observer
    const resizeObserver = new ResizeObserver(updateMapDimensions);
    if (mapContainerRef.current) {
      resizeObserver.observe(mapContainerRef.current);
    }

    // Cleanup
    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  if (loadingOrg) {
    return (
      <div className={styles.loadingContainer}>
        <CircularProgress />
      </div>
    );
  }

  const isLoading = loadingBins || loadingBinsForMap;
  return (
    <div className={styles.container}>
      <div className={styles.mainContent}>
        <InventoryMapBinList
          bins={binsForMap || []}
          selectedBinId={selectedBinId}
          onBinSelect={handleBinSelect}
          sort={sort}
          order={order}
        />

        <InventoryMapBinDetailSidebar
          selectedBin={selectedBin}
          selectedBinData={binsForMap.find(bin => bin.id === selectedBinId)}
          onClose={handleCloseSidebar}
        />

        {isLoading && (
          <div className={styles.loadingContainer}>
            <CircularProgress />
          </div>
        )}

        {!isLoading && binsForMap?.length > 0 && (
          <div className={styles.mapContainer} ref={mapContainerRef}>
            <InventoryMapFilters
              selectedBinType={selectedBinType}
              selectedMaterial={selectedIngredient}
              selectedLevelRange={selectedLevelRange}
              searchQuery={searchQuery}
              availableBinTypes={availableBinTypes}
              availableMaterials={availableMaterials}
              materialColorMap={materialColorMap}
              onBinTypeChange={setSelectedBinType}
              onMaterialChange={setSelectedIngredient}
              onLevelRangeChange={setSelectedLevelRange}
              onSearchChange={onSearchQueryChange}
            />

            <InventoryMapHighlightControls
              highlightMode={highlightMode}
              onHighlightModeChange={setHighlightMode}
              materialColorMap={materialColorMap}
              binsForMap={binsForMap}
            />

            <InventoryMapContainer
              bins={binsForMap}
              backgroundShapes={exampleBackgroundShapes}
              binStyles={binStyles}
              onBinClick={handleBinClick}
              width={mapDimensions.width}
              height={mapDimensions.height}
              showFillLevel={highlightMode === HighlightModes.FILL_LEVEL}
            />
          </div>
        )}
      </div>
    </div>
  );
}
